import React from 'react';
import { cn } from '@/lib/utils';

export interface ManagementHeaderProps {
  title: string;
  /** Optional control beside the title (e.g. step tour help). */
  titleAccessory?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Primary page actions (create, export, etc.) */
  actions?: React.ReactNode;
  /** Secondary toolbar below the title row — filters, navigation, counts */
  subheader?: React.ReactNode;
  className?: string;
}

/**
 * In-card page header — pinned to the top of the main content area; page body scrolls beneath.
 */
export function ManagementHeader({
  title,
  titleAccessory,
  subtitle,
  actions,
  subheader,
  className,
}: ManagementHeaderProps) {
  return (
    <div
      className={cn(
        'management-header shrink-0 border-y border-border bg-card',
        'px-4 pt-3 sm:px-6',
        subheader ? 'pb-0' : 'pb-3',
        className
      )}
    >
      <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <h1 className="truncate text-lg font-semibold leading-tight text-foreground sm:text-xl">
              {title}
            </h1>
            {titleAccessory}
          </div>
          {subtitle ? (
            <div className="mt-1 line-clamp-2 text-sm text-muted-foreground sm:line-clamp-none">
              {subtitle}
            </div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {subheader ? (
        <div
          className={cn(
            'management-subheader -mx-4 mt-3 border-t border-border bg-muted/10 px-4 pt-2.5 pb-2 dark:bg-muted/5',
            'sm:-mx-6 sm:px-6'
          )}
        >
          {subheader}
        </div>
      ) : null}
    </div>
  );
}

export default ManagementHeader;
